<template>
  <div class="chart-container">
    <canvas ref="chartCanvas" role="img" :aria-label="accessibleLabel"></canvas>
    <div v-if="error" class="chart-error">
      <p>{{ errorLabel }}</p>
    </div>
  </div>
</template>

<script>
import { Chart, registerables } from "chart.js";
import { markRaw } from "vue";
Chart.register(...registerables);

export default {
  name: "ChartComponent",
  props: {
    accessibleLabel: { type: String, default: "Chart" },
    errorLabel: { type: String, default: "Unable to display chart." },
    chartData: {
      type: Object,
      required: true,
      validator: (value) => {
        return value?.datasets?.length > 0;
      },
    },
    chartType: {
      type: String,
      default: "line",
      validator: (value) => {
        return ["line", "bar", "pie", "doughnut", "radar", "scatter"].includes(
          value
        );
      },
    },
    chartOptions: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      chartInstance: null,
      error: null,
      retryCount: 0,
      maxRetries: 5,
      debounceTimeout: null,
      retryTimeout: null,
      renderToken: 0,
      isUnmounted: false,
    };
  },
  methods: {
    mergeOptions(base, override) {
      if (Array.isArray(override)) {
        return [...override];
      }
      if (
        typeof base !== "object" ||
        base === null ||
        typeof override !== "object" ||
        override === null
      ) {
        return override === undefined ? base : override;
      }

      const merged = { ...base };
      Object.keys(override).forEach((key) => {
        merged[key] = this.mergeOptions(base[key], override[key]);
      });
      return merged;
    },
    clearScheduledWork() {
      if (this.debounceTimeout) {
        clearTimeout(this.debounceTimeout);
        this.debounceTimeout = null;
      }
      if (this.retryTimeout) {
        clearTimeout(this.retryTimeout);
        this.retryTimeout = null;
      }
    },
    destroyChart() {
      if (this.chartInstance) {
        try {
          this.chartInstance.stop();
          this.chartInstance.destroy();
        } catch (error) {
          console.warn("Failed to destroy chart instance cleanly", error);
        }
        this.chartInstance = null;
      }
    },
    createChart() {
      if (this.isUnmounted) {
        return;
      }
      this.clearScheduledWork();
      const currentRenderToken = ++this.renderToken;
      this.debounceTimeout = setTimeout(() => {
        if (this.isUnmounted || currentRenderToken !== this.renderToken) {
          return;
        }
        this.$nextTick(() => {
          if (this.isUnmounted || currentRenderToken !== this.renderToken) {
            return;
          }
          try {
            const canvas = this.$refs.chartCanvas;
            if (!(canvas instanceof HTMLCanvasElement) || !canvas.isConnected) {
              throw new Error("Canvas element not found");
            }

            const existingChart = Chart.getChart(canvas);
            if (existingChart && existingChart !== this.chartInstance) {
              existingChart.stop();
              existingChart.destroy();
            }

            const ctx = canvas.getContext("2d");
            if (!ctx) {
              throw new Error("Could not get canvas context");
            }

            if (!this.chartData?.datasets?.length) {
              throw new Error("No valid chart data available");
            }

            // Clone data to prevent external mutations
            const chartData = JSON.parse(JSON.stringify(this.chartData));
            const defaultOptions = {
              responsive: true,
              maintainAspectRatio: false,
              layout: {
                padding: {
                  top: 10,
                  right: 12,
                  bottom: 6,
                  left: 8,
                },
              },
              animation: {
                duration: 0,
              },
              elements: {
                line: {
                  capBezierPoints: true,
                },
              },
            };
            const mergedOptions = this.mergeOptions(
              defaultOptions,
              this.chartOptions || {}
            );
            mergedOptions.animation = false;

            if (
              this.chartInstance &&
              this.chartInstance.config.type === this.chartType
            ) {
              this.chartInstance.data = chartData;
              this.chartInstance.options = mergedOptions;
              this.chartInstance.update("none");
            } else {
              this.destroyChart();
              this.chartInstance = markRaw(
                new Chart(ctx, {
                  type: this.chartType,
                  data: chartData,
                  options: mergedOptions,
                })
              );
            }
            this.error = null; // Clear error if chart creation is successful
            this.retryCount = 0; // Reset retry count on success
          } catch (error) {
            if (this.isUnmounted || currentRenderToken !== this.renderToken) {
              return;
            }
            console.error("Error creating chart:", error);
            this.error = error.message || "Error creating chart";
            if (this.retryCount < this.maxRetries) {
              this.retryCount++;
              this.retryTimeout = setTimeout(() => {
                if (
                  !this.isUnmounted &&
                  currentRenderToken === this.renderToken
                ) {
                  this.createChart();
                }
              }, 300);
            }
          }
        });
      }, 70); // Short debounce to collapse rapid UI changes
    },
  },
  watch: {
    chartData: {
      handler(newVal) {
        if (!newVal?.datasets?.length) {
          this.clearScheduledWork();
          this.destroyChart();
          this.error = null;
          return;
        }
        this.createChart();
      },
      deep: true,
    },
    chartType() {
      this.createChart();
    },
    chartOptions: {
      handler() {
        this.createChart();
      },
      deep: true,
    },
  },
  mounted() {
    this.isUnmounted = false;
    this.createChart();
  },
  beforeUnmount() {
    this.isUnmounted = true;
    this.renderToken++;
    this.clearScheduledWork();
    this.destroyChart();
  },
  activated() {
    this.isUnmounted = false;
    this.createChart();
  },
  deactivated() {
    this.renderToken++;
    this.clearScheduledWork();
    this.destroyChart();
  },
};
</script>

<style scoped>
.chart-container {
  width: 100%;
  height: clamp(300px, 38vh, 440px);
  margin: auto;
  background: #fff;
  border-radius: 0;
  position: relative;
  padding: 0;
}

canvas {
  width: 100% !important;
  height: 100% !important;
}

.chart-error {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  color: #dc3545;
}

@media (max-width: 600px) {
  .chart-container {
    height: min(50vh, 340px);
    padding: 8px 8px 6px;
  }
}

@media (max-width: 380px) {
  .chart-container {
    height: min(46vh, 300px);
  }
}
</style>
